import { useState, useEffect } from "react";
import "./DownloadPage.css";
import { FiDownload } from "react-icons/fi";
import { authFetch } from "./authFetch";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LETTERS = [
  "#","A","B","C","D","E","F","G","H","I","J","K","L",
  "M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"
];

export default function DownloadPage() {
  const [activeSection, setActiveSection] = useState(null);
  const openSection = (s) => setActiveSection((prev) => (prev === s ? null : s));

  // =============================
  // CLINIC EXPORT STATES
  // =============================
  const [clinicInput, setClinicInput] = useState("");
  const [clinic, setClinic] = useState("");

  const [allClinics, setAllClinics] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState("");
  const [letterClinics, setLetterClinics] = useState([]);

  // =============================
  // LOAD CLINICS
  // =============================
  useEffect(() => {
    async function loadClinics() {
      try {
        const res = await authFetch(`${API_BASE}/admin/clinics`);
        const data = await res.json();
        const names = (data.items || []).map((i) => i.clinicName);
        names.sort((a, b) => a.localeCompare(b));
        setAllClinics(names);
      } catch (err) {
        console.log("Failed to load clinics");
      }
    }
    loadClinics();
  }, []);

  // =============================
  // ALPHABET CLICK → SHOW MODAL
  // =============================
  function handleLetterClick(letter) {
    setSelectedLetter(letter);

    let list = [];

    if (letter === "#") {
      list = allClinics.filter((c) => !/^[A-Za-z]/.test(c));
    } else {
      list = allClinics.filter((c) =>
        c.toUpperCase().startsWith(letter)
      );
    }

    setLetterClinics(list);
    setModalOpen(true);
  }

  // =============================
  // SEARCH → EXACT MATCH
  // =============================
  function handleClinicSearch() {
    const input = clinicInput.trim().toLowerCase();
    if (!input) return alert("Enter clinic name");

    const match = allClinics.find((c) => c.toLowerCase() === input);

    if (match) setClinic(match);
    else alert("Clinic not found");
  }

  // =============================
  // DOWNLOAD CLINIC EXPORT
  // =============================
  async function downloadClinicExport() {
    if (!clinic) return alert("Select a clinic");

    const url = `${API_BASE}/admin/export/clinic?clinic=${encodeURIComponent(clinic)}`;

    const res = await authFetch(url);

    if (!res.ok) {
      alert("Download failed");
      return;
    }

    const blob = await res.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = downloadUrl;
    a.download = `ClinicExport-${clinic}.csv`;
    a.click();

    window.URL.revokeObjectURL(downloadUrl);
  }

  // ======================================================
  //  BATCH EXPORT — ***COMMENTED OUT COMPLETELY***
  // ======================================================
  /*
  const [batchDate, setBatchDate] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchMsg, setBatchMsg] = useState("");

  async function loadBatches(d) {
    setBatchMsg("");
    setBatches([]);

    if (!d) return;

    try {
      const res = await authFetch(`${API_BASE}/admin/uploads/by-date?date=${d}`);
      const data = await res.json();

      setBatches(data.batches || []);
      if (!data.batches?.length) setBatchMsg("No batches found");
    } catch (err) {
      setBatchMsg("Failed to load batches");
    }
  }

  async function downloadBatch(batchId) {
    try {
      const res = await authFetch(`${API_BASE}/admin/export/batch/${batchId}`);

      if (!res.ok) {
        alert("Download failed");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `Batch-${batchId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed");
    }
  }
  */

  // =============================
  // RENDER
  // =============================
  return (
    <div className="download-wrapper">

      <h2 className="download-title">Data Export</h2>

      {/* ===============================
          CLINIC EXPORT SECTION
      =============================== */}
      <div className={`export-card clickable`} onClick={() => openSection("clinic")}>
        <h3>Clinic-Based Export</h3>
        <span className="expand-arrow">{activeSection === "clinic" ? "▲" : "▼"}</span>
      </div>

      {activeSection === "clinic" && (
        <div className="export-card">

          {/* Alphabet Bar */}
          <div className="alphabet-wrapper">
            <div className="alphabet-inside">
              {LETTERS.map((letter) => (
                <button
                  key={letter}
                  className={`letter-btn ${selectedLetter === letter ? "letter-selected" : ""}`}
                  onClick={() => handleLetterClick(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div className="clinic-search-row">
            <input
              type="text"
              placeholder="Search or select a clinic"
              value={clinicInput}
              onChange={(e) => setClinicInput(e.target.value)}
            />
            <button className="clinic-search-btn" onClick={handleClinicSearch}>
              Search
            </button>
          </div>

          {/* Selected Clinic Chip */}
          {clinic && (
            <div className="selected-clinic-chip">
              <span>{clinic}</span>
              <button
                className="chip-close-btn"
                onClick={() => {
                  setClinic("");
                  setClinicInput("");
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Download */}
          <button
            className="download-btn"
            onClick={downloadClinicExport}
            disabled={!clinic}
          >
            Download
          </button>
        </div>
      )}

      {/* ======================================================
           BATCH EXPORT — ***REMOVED FROM UI***
      ======================================================= */}

      {/* 
      <div className={`export-card clickable`} onClick={() => openSection("batch")}>
        <h3>Batch-Based Export</h3>
        <span className="expand-arrow">{activeSection === "batch" ? "▲" : "▼"}</span>
      </div>

      {activeSection === "batch" && (
        <div className="export-card">
          <div className="form-block">
            <label>Date</label>
            <input
              type="date"
              value={batchDate}
              onChange={(e) => {
                setBatchDate(e.target.value);
                loadBatches(e.target.value);
              }}
            />
          </div>

          {batchMsg && <p className="result-msg">{batchMsg}</p>}

          <div className="batch-list">
            {batches.map((b, i) => (
              <button
                key={i}
                className="batch-btn"
                onClick={() => downloadBatch(b.batchId)}
              >
                <span>{b.batchId}</span>
                <FiDownload size={18} color="#2563eb" />
              </button>
            ))}
          </div>
        </div>
      )}
      */}

      {/* ===============================
          MODAL
      =============================== */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="clinic-modal">

            <div className="modal-header">
              <span>Clinics starting with "{selectedLetter}"</span>
              <button className="modal-close-btn" onClick={() => setModalOpen(false)}>
                ×
              </button>
            </div>

            <div className="clinic-modal-scroll">
              {letterClinics.length === 0 ? (
                <div className="modal-empty">No clinics found</div>
              ) : (
                letterClinics.map((name, i) => (
                  <div
                    key={i}
                    className="clinic-modal-item"
                    onClick={() => {
                      setClinic(name);
                      setClinicInput(name);
                      setModalOpen(false);
                    }}
                  >
                    {name}
                  </div>
                ))
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
